import { pageTitle } from '@palaccount/config';
import type { PartnerResponse } from '@palaccount/contracts';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { PartnerStatusChip, PartnerTypeChips } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CrmTabs } from '@/features/crm/crm-tabs';

import { Pager } from '@/components/pager';
import { usePagedState, usePageRange } from '@/lib/paging';

import { usePagedPartners } from './api';
import { usePartnerGroups } from './groups-api';

const columnHelper = createColumnHelper<PartnerResponse>();

/**
 * MD-01 §4 — the partner list: one row per partner with code, name, type
 * chips, currency, what they owe us, and the worst-first status chip. Search
 * matches name or code; the filters are the contract's own (type,
 * include-inactive). Money is trailing-aligned in the figure font.
 */
export function PartnersListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [type, setType] = useState<'' | 'customer' | 'supplier'>('');
  // MD-01 field 4's "used for reporting" — the group slice, URL-seeded so the
  // count on the groups screen can link straight here.
  const [searchParams] = useSearchParams();
  const [group, setGroup] = useState(searchParams.get('group') ?? '');
  const [includeInactive, setIncludeInactive] = useState(false);
  /** NFR-42 — the page is in the URL, so a link to page 3 is shareable. */
  const paging = usePagedState();

  useEffect(() => {
    document.title = pageTitle(t('partners.title'));
  }, [t]);

  const { data, isPending, isError } = usePagedPartners(
    { q: q.trim() || undefined, type: type || undefined, group: group || undefined, includeInactive },
    paging.page,
    paging.pageSize,
  );
  const { data: groups } = usePartnerGroups(true);
  const partners = data?.partners;
  const range = usePageRange(data, paging.pageSize);

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: () => t('partners.colCode'),
        cell: (info) => <Ltr value={info.getValue()} className="font-mono text-muted-foreground" />,
      }),
      columnHelper.accessor('name', {
        header: () => t('partners.colName'),
        cell: (info) => <span className="font-medium">{info.getValue()}</span>,
      }),
      columnHelper.display({
        id: 'types',
        header: () => t('partners.colType'),
        cell: ({ row }) => <PartnerTypeChips partner={row.original} />,
      }),
      columnHelper.accessor('currency', {
        header: () => t('partners.colCurrency'),
        cell: (info) => <Ltr value={info.getValue()} className="font-mono" />,
      }),
      columnHelper.accessor('owedToUs', {
        header: () => <div className="text-end">{t('partners.colOwedToUs')}</div>,
        cell: (info) => (
          <div className="text-end">
            <Amount value={info.getValue()} currency={info.row.original.currency} />
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: () => t('partners.colStatus'),
        cell: (info) => <PartnerStatusChip status={info.getValue()} />,
      }),
    ],
    [t],
  );

  const table = useReactTable({
    data: partners ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="grid gap-4">
      <CrmTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('partners.title')}</h2>
        <Gated permission="crm.create" reasonKey="perm.cannot.createPartners">
          <Button asChild>
            <Link to="/partners/new">
              <Plus aria-hidden /> {t('common.new')}
            </Link>
          </Button>
        </Gated>
      </div>

      <Card>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-[320px]">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                aria-label={t('partners.searchLabel')}
                placeholder={t('partners.searchPh')}
                value={q}
                onChange={(event) => paging.change(setQ)(event.target.value)}
                className="h-10 ps-9 text-sm"
              />
            </div>
            <NativeSelect
              aria-label={t('partners.filterType')}
              value={type}
              onChange={(event) => paging.change(setType)(event.target.value as typeof type)}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('partners.filterTypeAll')}</option>
              <option value="customer">{t('partners.typeCustomer')}</option>
              <option value="supplier">{t('partners.typeSupplier')}</option>
            </NativeSelect>
            {/* MD-01 field 4: "a grouping used for reporting" — the slice. */}
            <NativeSelect
              aria-label={t('partners.filterGroup')}
              value={group}
              onChange={(event) => paging.change(setGroup)(event.target.value)}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('partners.filterGroupAll')}</option>
              {(groups ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </NativeSelect>
            <Label className="flex items-center gap-2 text-sm font-normal">
              <Checkbox
                checked={includeInactive}
                onCheckedChange={(checked) =>
                  paging.change(setIncludeInactive)(checked === true)
                }
              />
              {t('partners.filterInactive')}
            </Label>
            <Link
              to="/partners/groups"
              className="ms-auto text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              {t('pgroups.manage')}
            </Link>
          </div>

          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : table.getRowModel().rows.length === 0 ? (
            // MD-01 §15 "Empty": the day-one empty state, with its + New button.
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-muted-foreground">{t('partners.empty')}</p>
              <Gated permission="crm.create" reasonKey="perm.cannot.createPartners">
                <Button asChild variant="outline">
                  <Link to="/partners/new">
                    <Plus aria-hidden /> {t('common.new')}
                  </Link>
                </Button>
              </Gated>
            </div>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => void navigate(`/partners/${row.original.id}`)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. This is the
              screen PAGINATION.md carried as its CONTROL: 2,001 rows in the
              DOM, the one list the paging waves could not touch because the
              route behind it fed every document editor's picker. */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('partners.title')}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
